// @ts-strict-ignore
import { Icon, IconName } from '@usealloy/component-library';
import React from 'react';

import type {
  OutcomeIcon,
  Transaction,
} from '~/entities/evaluations/transaction/history/types';
import type { OUTCOMES } from '~/utils/outcomes';

/*
  Outcome and FontAwesome constants used to construct icon classnames and tooltips
  Copied from Daffy/compliance/mixins.py
*/
const APPROVED_OUTCOME_STRINGS = ['yes', 'true', 'approve', 'approved'] as const;
const DENIED_OUTCOME_STRINGS = ['no', 'false', 'deny', 'denied'] as const;
const MANUAL_REVIEW_OUTCOME_STRINGS = ['manual review'] as const;

const OUTCOME_ICONS = {
  none: {
    className: 'fas fa-fw fa-lg fa-do-not-enter grey',
    IconComponent: () => <Icon title="No outcome" color="var(--cl-gray-400)" icon={IconName.NoOutcome} />,
  },
  approved: {
    className: 'fas fa-fw fa-lg fa-check-circle green',
    IconComponent: () => <Icon title="Approved" color="var(--cl-green-400)" icon={IconName.Approved} />,
  },
  denied: {
    className: 'fas fa-fw fa-lg fa-times-circle red',
    IconComponent: () => <Icon title="Denied" color="var(--cl-red-400)" icon={IconName.Declined} />,
  },
  manualReview: {
    className: 'fas fa-fw fa-lg fa-pen-square yellow',
    IconComponent: () => <Icon title="Manual Review" color="var(--cl-yellow-400)" icon={IconName.Review} />,
  },
  unknown: {
    className: 'fas fa-fw fa-lg fa-question-circle purple',
    IconComponent: () => <Icon title="Unknown" color="var(--cl-purple-400)" icon={IconName.Question} />,
  },
};

type OutcomeApproved = typeof APPROVED_OUTCOME_STRINGS[number];
type OutcomeDenied = typeof DENIED_OUTCOME_STRINGS[number];
type OutcomeManualReview = typeof MANUAL_REVIEW_OUTCOME_STRINGS[number];

function getOutcomeFontAwesome(outcome?: OUTCOMES | null): OutcomeIcon {
  if (!outcome) {
    return OUTCOME_ICONS.none;
  }

  const outcomeLowerCase = outcome.toLowerCase();

  if (APPROVED_OUTCOME_STRINGS.includes(outcomeLowerCase as OutcomeApproved)) {
    return OUTCOME_ICONS.approved;
  }

  if (DENIED_OUTCOME_STRINGS.includes(outcomeLowerCase as OutcomeDenied)) {
    return OUTCOME_ICONS.denied;
  }

  if (MANUAL_REVIEW_OUTCOME_STRINGS.includes(outcomeLowerCase as OutcomeManualReview)) {
    return OUTCOME_ICONS.manualReview;
  }

  return OUTCOME_ICONS.unknown;
}

/*
This helper function implements the variable mapping handled by the
SELECT_TRANSACTION_HISTORY query in Daffy/compliance/transactions_evaluations/sql.py
The above query is no longer being used in the TransactionHistory component,
but we want to ensure that incoming transactions data adheres to the same naming expectations
*/
const getFormattedTransactions = (rawTransactions): Transaction[] => {
  if (!rawTransactions?.length) {
    return [];
  }

  return rawTransactions.map(t => ({
    ...t, // Include other fields that don't need formatting
    amount: t.amount ? Number(t.amount) : null,
    account_number: t.source_bank_account_number || null,
    'Counter-Party Country Code': t.target_bank_country_code || null,
    'Counter-Party Name': t.target_bank_account_name || null,
    'Counter-Party Sanctions': t.matches >= 0 ? t.matches : null,
    date: t.created_at || null,
    MCC: t.target_bank_merchant_category_code || null,
    'MCC Description': t.target_bank_mcc_description || null,
    outcome_fa: getOutcomeFontAwesome(t.outcome || null),
    product_name: t.source_bank_product_name || null,
    'Transaction ID': t.external_transaction_identifier,
    transaction_id: t.external_transaction_identifier,
    transaction_code: t.type_code || null,
    transaction_type: t.type_description || null,
    transaction_description: t.description || null,
    metadata: t.meta || null,
  }));
};

const formatMetadata = value => (
  (value?.toString && typeof value !== 'object')
    ? value.toString()
    : JSON.stringify(value)
);

export {
  formatMetadata,
  getFormattedTransactions,
  getOutcomeFontAwesome,
};
